import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatDateIST } from '../../utils/dateUtils';
import { 
  Plus, User, Phone, Calendar, Trash2, Loader2, ShieldCheck, Edit2,
  LayoutDashboard, Users, CreditCard, FileBarChart, Settings, UserCog, Eye, Pencil,
  UserCheck, UserPlus, Bell
} from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';

// Icon mapping for dynamic permissions
const iconMap = {
  LayoutDashboard, Users, CreditCard, FileBarChart, Settings, UserCog, Eye, Pencil,
  UserCheck, UserPlus, Bell
};

// Fallback permission labels (used if API fails to load)
const fallbackPermissionLabels = {
  dashboard: { label: 'Dashboard', icon: 'LayoutDashboard', description: 'View dashboard stats' },
  users: { label: 'Users', icon: 'Users', description: 'View user list & details' },
  transactions: { label: 'Transactions', icon: 'CreditCard', description: 'View all transactions' },
  reports: { label: 'Reports', icon: 'FileBarChart', description: 'View P&L, Outstanding, Daily Reports' },
  settings: { label: 'Settings', icon: 'Settings', description: 'Referral settings, rate override' },
  sub_admins: { label: 'Sub-Admins', icon: 'UserCog', description: 'Manage sub-admin users' },
  kyc: { label: 'KYC Identities', icon: 'UserCheck', description: 'Track linked accounts by KYC' },
  referrals: { label: 'Employee Referrals', icon: 'UserPlus', description: 'Manage employee referral codes' },
  notifications: { label: 'Notifications', icon: 'Bell', description: 'Send push notifications' }
};

const fallbackDefaults = {
  dashboard: true,
  users: true,
  transactions: true,
  reports: true,
  settings: false,
  sub_admins: false,
  kyc: false,
  referrals: false,
  notifications: true,
  view_only: true
};

export const AdminSubAdminsPage = () => {
  const navigate = useNavigate();
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  
  // Dynamic permissions from backend
  const [permissionSchema, setPermissionSchema] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState(fallbackDefaults);
  
  const [newSubAdmin, setNewSubAdmin] = useState({ 
    name: '', 
    phone: '', 
    permissions: { ...fallbackDefaults }
  });
  const [editingAdmin, setEditingAdmin] = useState(null);

  // Fetch permission schema from backend
  const fetchPermissionSchema = useCallback(async () => {
    try {
      const response = await adminAPI.getPermissionsSchema();
      const { permissions, defaults } = response.data;
      
      // Convert array to object format for easier lookup
      const schemaObj = {};
      permissions.forEach(p => {
        schemaObj[p.key] = {
          label: p.label,
          icon: p.icon,
          description: p.description,
          category: p.category
        };
      });
      
      setPermissionSchema(permissions);
      setDefaultPermissions(defaults);
      setNewSubAdmin(prev => ({ ...prev, permissions: { ...defaults } }));
    } catch (error) {
      console.error('Failed to load permission schema, using fallbacks:', error);
      // Use fallback values
      setPermissionSchema(Object.entries(fallbackPermissionLabels).map(([key, val]) => ({
        key,
        ...val
      })));
    }
  }, []);

  const fetchSubAdmins = useCallback(async () => {
    try {
      const response = await adminAPI.getSubAdmins();
      setSubAdmins(response.data.sub_admins || []);
    } catch (error) {
      toast.error('Failed to load sub-admins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissionSchema();
    fetchSubAdmins();
  }, [fetchPermissionSchema, fetchSubAdmins]);

  const handleCreate = async () => {
    if (!newSubAdmin.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!newSubAdmin.phone || newSubAdmin.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setCreateLoading(true);
    try {
      await adminAPI.createSubAdmin({
        name: newSubAdmin.name.trim(),
        phone: newSubAdmin.phone,
        permissions: newSubAdmin.permissions
      });
      toast.success('Sub-admin created successfully');
      setCreateOpen(false);
      setNewSubAdmin({ name: '', phone: '', permissions: { ...defaultPermissions } });
      fetchSubAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sub-admin');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin({
      ...admin,
      permissions: admin.permissions || { ...defaultPermissions }
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingAdmin) return;
    
    setEditLoading(true);
    try {
      await adminAPI.updateSubAdmin(editingAdmin.phone, {
        name: editingAdmin.name,
        permissions: editingAdmin.permissions
      });
      toast.success('Sub-admin updated successfully');
      setEditOpen(false);
      setEditingAdmin(null);
      fetchSubAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sub-admin');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (phone) => {
    try {
      await adminAPI.deleteSubAdmin(phone);
      toast.success('Sub-admin deleted');
      fetchSubAdmins();
    } catch (error) {
      toast.error('Failed to delete sub-admin');
    }
  };

  const PermissionToggle = ({ permissions, setPermissions, permKey, schema }) => {
    // Find config from schema or use fallback
    const schemaItem = schema.find(s => s.key === permKey) || fallbackPermissionLabels[permKey];
    if (!schemaItem) return null;
    
    const Icon = iconMap[schemaItem.icon] || LayoutDashboard;
    
    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">{schemaItem.label}</p>
            <p className="text-xs text-muted-foreground">{schemaItem.description}</p>
          </div>
        </div>
        <Switch
          checked={permissions[permKey] || false}
          onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, [permKey]: checked }))}
        />
      </div>
    );
  };

  const PermissionForm = ({ permissions, setPermissions }) => {
    // Filter out view_only from the toggles (it's handled separately)
    const toggleablePermissions = permissionSchema.filter(p => p.key !== 'view_only');
    
    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <Label className="text-sm font-semibold">Section Access</Label>
          <Card className="border-gray-200">
            <CardContent className="p-3">
              {toggleablePermissions.map(perm => (
                <PermissionToggle
                  key={perm.key}
                  permissions={permissions}
                  setPermissions={setPermissions}
                  permKey={perm.key}
                  schema={permissionSchema}
                />
              ))}
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-1">
          <Label className="text-sm font-semibold">Access Level</Label>
          <Card className={`border-2 ${permissions.view_only ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {permissions.view_only ? (
                    <Eye className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Pencil className="w-5 h-5 text-green-600" />
                  )}
                  <div>
                    <p className="font-medium">
                      {permissions.view_only ? 'View Only' : 'Full Access'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {permissions.view_only 
                        ? 'Can view data and download CSVs only' 
                        : 'Can view, edit, and perform all actions'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={!permissions.view_only}
                  onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, view_only: !checked }))}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const getPermissionBadges = (permissions) => {
    if (!permissions) return null;
    
    // Get enabled sections from permission schema
    const sections = permissionSchema
      .filter(p => p.key !== 'view_only' && permissions[p.key])
      .map(p => p.key);
    const accessLevel = permissions.view_only ? 'View Only' : 'Full Access';
    
    return (
      <div className="flex flex-wrap gap-1 mt-2">
        <Badge variant="outline" className={permissions.view_only ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-green-300 text-green-700 bg-green-50'}>
          {permissions.view_only ? <Eye className="w-3 h-3 mr-1" /> : <Pencil className="w-3 h-3 mr-1" />}
          {accessLevel}
        </Badge>
        {sections.map(s => {
          const schemaItem = permissionSchema.find(p => p.key === s) || fallbackPermissionLabels[s];
          return (
            <Badge key={s} variant="secondary" className="text-xs">
              {schemaItem?.label || s}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-subadmins-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <h1 className="font-serif text-xl text-navy font-semibold">Sub-Admin Management</h1>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gold-gradient text-navy">
                <Plus className="w-4 h-4 mr-2" />
                Add Sub-Admin
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-navy">Create Sub-Admin</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="Enter name"
                    value={newSubAdmin.name}
                    onChange={(e) => setNewSubAdmin(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                      +91
                    </span>
                    <Input
                      placeholder="10-digit number"
                      value={newSubAdmin.phone}
                      onChange={(e) => setNewSubAdmin(prev => ({ 
                        ...prev, 
                        phone: e.target.value.replace(/\D/g, '').slice(0, 10) 
                      }))}
                      className="pl-12"
                    />
                  </div>
                </div>
                
                <PermissionForm 
                  permissions={newSubAdmin.permissions}
                  setPermissions={(fn) => setNewSubAdmin(prev => ({ 
                    ...prev, 
                    permissions: typeof fn === 'function' ? fn(prev.permissions) : fn 
                  }))}
                />
                
                <Button 
                  onClick={handleCreate} 
                  disabled={createLoading}
                  className="w-full gold-gradient text-navy"
                >
                  {createLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 mr-2" />
                  )}
                  Create Sub-Admin
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Info */}
        <Card className="mb-6 bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-purple-600 mt-0.5" />
              <div>
                <p className="font-medium text-purple-700">Sub-Admin Access Control</p>
                <p className="text-sm text-purple-600/80 mt-1">
                  Configure which sections each sub-admin can access. View Only users can view data and download CSVs but cannot make changes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sub-Admin List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
        ) : subAdmins.length === 0 ? (
          <Card className="border-gold/20">
            <CardContent className="py-12 text-center">
              <User className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No sub-admins yet</p>
              <p className="text-sm text-muted-foreground">Click "Add Sub-Admin" to create one</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {subAdmins.map((admin) => (
              <Card key={admin.phone} className="border-gold/20">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-navy">{admin.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          <span>+91 {admin.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <Calendar className="w-3 h-3" />
                          <span>Added {formatDateIST(admin.created_at)}</span>
                        </div>
                        {getPermissionBadges(admin.permissions)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleEdit(admin)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove Sub-Admin?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove {admin.name}'s admin access. They will no longer be able to access the admin dashboard.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(admin.phone)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy">Edit Sub-Admin</DialogTitle>
          </DialogHeader>
          {editingAdmin && (
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  placeholder="Enter name"
                  value={editingAdmin.name}
                  onChange={(e) => setEditingAdmin(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  value={`+91 ${editingAdmin.phone}`}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              
              <PermissionForm 
                permissions={editingAdmin.permissions}
                setPermissions={(fn) => setEditingAdmin(prev => ({ 
                  ...prev, 
                  permissions: typeof fn === 'function' ? fn(prev.permissions) : fn 
                }))}
              />
              
              <Button 
                onClick={handleUpdate} 
                disabled={editLoading}
                className="w-full gold-gradient text-navy"
              >
                {editLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ShieldCheck className="w-4 h-4 mr-2" />
                )}
                Update Sub-Admin
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubAdminsPage;
